import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ShieldCheck, Loader2 } from "lucide-react";
import { useSyncRequest } from "./useSyncRequest";
import { useAuth } from "./auth-context";

type Mode = "choice" | "syncing";

/**
 * Mounted once, high in the tree (alongside TrustedDeviceApprovalProvider).
 * This is the actual fix for the destructive-overwrite bug: previously, a
 * session with no local identity and no usable backup would silently
 * generate and publish a brand new one, overwriting the account's real key
 * with zero warning. Now it stops and asks first, whenever the account is
 * genuinely active elsewhere.
 */
export function IdentityChoiceDialog() {
  const { pendingIdentityChoice, confirmStartFresh, dismissIdentityChoice } = useAuth();
  const [mode, setMode] = useState<Mode>("choice");
  const { phase, errorMessage, reset } = useSyncRequest(mode === "syncing");

  // Side effects belong in an effect, not inline during render - reacting to
  // the hook's own phase changes here rather than calling dismiss() directly
  // in the render body.
  useEffect(() => {
    if (phase === "done") {
      dismissIdentityChoice();
    }
  }, [phase, dismissIdentityChoice]);

  if (!pendingIdentityChoice) return null;

  function handleBack() {
    reset();
    setMode("choice");
  }

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>This account is active elsewhere</DialogTitle>
        </DialogHeader>

        {mode === "choice" && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <ShieldAlert className="h-10 w-10 text-amber-500" />
            <p className="text-sm text-muted-foreground">
              This account is already logged in on another device. Sync from there to bring over your existing
              conversations, or start fresh here - starting fresh means older messages won't be readable on this
              device.
            </p>
            <div className="flex w-full flex-col gap-2">
              <Button onClick={() => setMode("syncing")}>Sync from that device</Button>
              <Button variant="outline" onClick={() => confirmStartFresh()}>
                Start fresh on this device
              </Button>
            </div>
          </div>
        )}

        {mode === "syncing" && phase === "waiting" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <ShieldCheck className="h-10 w-10 text-foreground" />
            <p className="text-sm text-muted-foreground">
              Open Sksnap on your other device and approve this request from there.
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <Button variant="outline" size="sm" onClick={handleBack}>
              Back
            </Button>
          </div>
        )}

        {mode === "syncing" && phase === "completing" && (
          <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Syncing your conversations…</p>
          </div>
        )}

        {mode === "syncing" && (phase === "denied" || phase === "expired" || phase === "error") && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {phase === "denied" && "The sync request was denied from your other device."}
              {phase === "expired" && "This request expired before it was approved."}
              {phase === "error" && errorMessage}
            </p>
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
